/**
 * Standard Storefront Event : `CartLinesUpdateEvent`.
 *
 * Traduit les mutations de panier du theme en evenements que le Web Pixels
 * Manager de Shopify comprend, donc en `product_added_to_cart` dans les
 * rapports natifs.
 *
 * Deux sources, parce que le theme a deux chemins d'ajout distincts :
 *
 *  1. `PUB_SUB_EVENTS.cartUpdate` — le chemin standard Dawn, publie par
 *     `assets/product-form.js:72`, `assets/cart.js:314`,
 *     `assets/quick-add-bulk.js:131` et `assets/quick-order-list.js:346`.
 *
 *  2. `theme:cart-lines-added` — evenement DOM emis par
 *     `blocks/product-buy-buttons-advanced.liquid` et
 *     `blocks/product-sticky-atc-advanced.liquid`, qui POSTent les produits
 *     complementaires et les cadeaux de variante par un `fetch` autonome
 *     sans jamais publier `cartUpdate`. Sans ce pont, ces lignes — celles a
 *     marge additionnelle — n'existent dans aucune mesure.
 *
 * Modele : shopify-theme-horizon/assets/product-form.js:439-451
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { isDesignMode, guarded } from '@theme/standard-events-utils';

if (!isDesignMode()) {
  bridgeThemePubSub();
  bridgeSilentAdditions();
}

/* ------------------------------------------------------------- helpers */

/**
 * Une reponse `/cart/change|update.js` est un panier complet ; une reponse
 * `/cart/add.js` est la ligne ajoutee. Seul `item_count` discrimine de
 * maniere fiable, comme le documente `assets/cart-drawer-progress-bar.js:136`.
 *
 * @param {any} data
 * @returns {boolean}
 */
function isFullCart(data) {
  return Boolean(data && Array.isArray(data.items) && data.item_count !== undefined);
}

/**
 * Shopify attend le panier entier pour resoudre la promesse de l'evenement.
 * Quand on ne l'a pas deja sous la main, on le relit.
 *
 * @param {any} maybeCart
 * @returns {Promise<any>}
 */
function resolveCart(maybeCart) {
  if (isFullCart(maybeCart)) return Promise.resolve(maybeCart);

  const base = (window.routes && window.routes.cart_url) || '/cart';

  return fetch(`${base}.js`, { headers: { Accept: 'application/json' } }).then((response) =>
    response.json()
  );
}

/**
 * @param {'add'|'update'|'remove'} action
 * @param {'product'|'cart'} context
 * @param {Array<object>} lines
 * @param {any} knownCart
 */
function dispatchCartLines(action, context, lines, knownCart) {
  if (!lines.length) return;

  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context,
      lines,
      promise: deferred.promise,
    })
  );

  resolveCart(knownCart)
    .then((cart) =>
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'breeq-standard-cart-events' },
      })
    )
    .catch(deferred.reject);
}

/** @param {any} line */
function toAddedLine(line) {
  return {
    merchandiseId: String(line.variant_id || line.id || ''),
    quantity: Number(line.quantity) || 1,
  };
}

/* ------------------------------------------------- 1. pub/sub du theme */

function bridgeThemePubSub() {
  if (typeof subscribe !== 'function') return;

  // `PUB_SUB_EVENTS` est un `const` de portee lexicale globale declare par
  // un script classique : visible ici, mais absent de `window`.
  const eventName =
    typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : 'cart-update';

  let knownQuantities = indexCart(window.ThemeAnalytics && window.ThemeAnalytics.cart);

  subscribe(
    eventName,
    guarded((payload) => {
      const data = payload && payload.cartData;
      if (!data) return;

      if (isFullCart(data)) {
        const next = indexCart(data);
        const { added, removed } = diff(knownQuantities, next);
        knownQuantities = next;

        dispatchCartLines('add', 'cart', added, data);
        dispatchCartLines('remove', 'cart', removed, data);
        return;
      }

      const lines = Array.isArray(data.items) ? data.items : [data];
      lines.forEach((line) => bumpKnown(knownQuantities, line));
      dispatchCartLines('add', 'product', lines.map(toAddedLine), null);
    })
  );

  /** @param {any} cart */
  function indexCart(cart) {
    const map = Object.create(null);
    const items = cart && Array.isArray(cart.items) ? cart.items : [];

    items.forEach((item) => {
      const key = item.key || String(item.variant_id || item.id);
      map[key] = { quantity: Number(item.quantity) || 0, line: item };
    });
    return map;
  }

  /** @param {object} map @param {any} line */
  function bumpKnown(map, line) {
    const key = line.key || String(line.variant_id || line.id);
    const before = map[key] ? map[key].quantity : 0;
    const reported = Number(line.quantity) || 0;

    map[key] = { quantity: Math.max(reported, before + 1), line };
  }

  /** @param {object} before @param {object} after */
  function diff(before, after) {
    const added = [];
    const removed = [];

    Object.keys(after).forEach((key) => {
      const gained = after[key].quantity - (before[key] ? before[key].quantity : 0);
      if (gained > 0) added.push({ id: key, quantity: gained });
    });
    Object.keys(before).forEach((key) => {
      const lost = before[key].quantity - (after[key] ? after[key].quantity : 0);
      if (lost > 0) removed.push({ id: key, quantity: lost });
    });

    return { added, removed };
  }
}

/* ------------------------------------- 2. ajouts silencieux des blocs ATC */

function bridgeSilentAdditions() {
  document.addEventListener(
    'theme:cart-lines-added',
    guarded((event) => {
      const response = event.detail && event.detail.response;
      const lines = response && Array.isArray(response.items) ? response.items : [];

      dispatchCartLines('add', 'product', lines.map(toAddedLine), null);
    })
  );
}
